import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ComplianceReport, IssueLink } from '@cls/shared';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { useRolePerms } from '../features/auth/useRolePerms.js';
import { WorkerDetail } from '../components/domain/WorkerDetail.js';
import { ProjectDetail } from '../components/domain/ProjectDetail.js';
import { ExpiredCertsSection } from '../features/compliance/sections/ExpiredCertsSection.js';
import { ExpiringCertsSection } from '../features/compliance/sections/ExpiringCertsSection.js';
import { MissingCertsSection } from '../features/compliance/sections/MissingCertsSection.js';
import { SuppliersSection } from '../features/compliance/sections/SuppliersSection.js';
import { UnsignedDocsSection } from '../features/compliance/sections/UnsignedDocsSection.js';
import { GeofenceSection } from '../features/compliance/sections/GeofenceSection.js';
import { DataQualitySection } from '../features/compliance/sections/DataQualitySection.js';
import { exportEmail, exportExcel, exportPdf } from '../features/compliance/exporters.js';

type Tab = 'overview' | 'expired' | 'expiring' | 'missing' | 'suppliers' | 'docs' | 'geofence' | 'dataquality';

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'overview', label: '📊 Overview' },
  { key: 'expired', label: '✗ Expired Certs' },
  { key: 'expiring', label: '⚠ Expiring 30d' },
  { key: 'missing', label: '✗ Missing Certs' },
  { key: 'suppliers', label: '🛠 Suppliers' },
  { key: 'docs', label: '✍ Unsigned Docs' },
  { key: 'geofence', label: '📍 Geofence' },
  { key: 'dataquality', label: '🔍 Data Quality' },
];

export function Compliance() {
  const { canExport, isInternal } = useRolePerms();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('overview');
  const [detailWorkerId, setDetailWorkerId] = useState<string | null>(null);
  const [detailProjectId, setDetailProjectId] = useState<string | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery<ComplianceReport>({
    queryKey: ['compliance', 'report'],
    queryFn: () => api.get<ComplianceReport>('/compliance/report').then((r) => r.data),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['compliance', 'report'] });
    toast.info('Refreshing…');
  };

  const reportTitle = `${data?.banner?.companyShortName ?? 'CLS'} Compliance Report`;
  const onPdf = () => exportPdf(reportRef.current, reportTitle);
  const onExcel = () => {
    if (!data) return;
    exportExcel(data);
  };
  const onEmail = () => exportEmail(reportTitle);

  const onWorker = (id: string) => setDetailWorkerId(id);
  const onIssueClick = (link: IssueLink) => {
    switch (link.kind) {
      case 'worker':
        setDetailWorkerId(link.id);
        break;
      case 'project':
        setDetailProjectId(link.id);
        break;
      case 'contact':
        navigate('/contacts');
        break;
      case 'customer':
        navigate('/customers');
        break;
      case 'supplier':
        navigate('/suppliers');
        break;
    }
  };

  const banner = data?.banner;
  const expiredCerts = data?.expiredCerts ?? [];
  const expiring30 = data?.expiring30 ?? [];
  const missingMandatory = data?.missingMandatory ?? [];
  const nonCompliantSuppliers = data?.nonCompliantSuppliers ?? [];
  const unsignedDocs = data?.unsignedDocs ?? [];
  const geofenceAnomalies = data?.geofenceAnomalies ?? [];
  const dataAudit = data?.dataAudit;

  const KPI_CARDS: Array<{ label: string; val: number; color: string; bg: string; t: Tab }> = dataAudit
    ? [
        { label: 'Expired Certs', val: expiredCerts.length, color: 'var(--danger)', bg: 'var(--danger-dim)', t: 'expired' },
        { label: 'Expiring 30d', val: expiring30.length, color: 'var(--warn)', bg: 'var(--warn-dim)', t: 'expiring' },
        { label: 'Missing Certs', val: missingMandatory.length, color: 'var(--danger)', bg: 'var(--danger-dim)', t: 'missing' },
        { label: 'Non-Compliant Suppliers', val: nonCompliantSuppliers.length, color: 'var(--warn)', bg: 'var(--warn-dim)', t: 'suppliers' },
        { label: 'Unsigned Docs', val: unsignedDocs.length, color: 'var(--info)', bg: 'var(--info-dim)', t: 'docs' },
        { label: 'Geofence Alerts', val: geofenceAnomalies.length, color: 'var(--warn)', bg: 'var(--warn-dim)', t: 'geofence' },
        {
          label: 'Data Quality',
          val: dataAudit.total,
          color: dataAudit.totalCritical > 0 ? 'var(--danger)' : 'var(--warn)',
          bg: dataAudit.totalCritical > 0 ? 'var(--danger-dim)' : 'var(--warn-dim)',
          t: 'dataquality',
        },
      ]
    : [];

  return (
    <div className="page" id="page-compliance">
      <div className="hbar">
        <div>
          <div className="ph">📊 Monthly Compliance Report</div>
          <div className="ps">One-click board-ready compliance overview</div>
        </div>
        <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
          {canExport ? (
            <button className="btn btn-ghost btn-sm" onClick={onPdf} title="Export PDF">
              🖨 PDF
            </button>
          ) : null}
          {canExport ? (
            <button className="btn btn-ghost btn-sm" onClick={onExcel} title="Export Excel">
              📄 Excel
            </button>
          ) : null}
          {isInternal ? (
            <button className="btn btn-ghost btn-sm" onClick={onEmail} title="Email Report">
              📧 Email
            </button>
          ) : null}
          <button className="btn btn-primary btn-sm" onClick={refresh}>
            ↺ Refresh
          </button>
        </div>
      </div>

      <div id="comp-tabs" style={{ display: 'flex', gap: 4, padding: '8px 16px', flexWrap: 'wrap', marginBottom: 12 }}>
        {TABS.map((t) => (
          <span
            key={t.key}
            id={`comp-tab-${t.key}`}
            className={`ts-tab${tab === t.key ? ' ts-tab-active' : ''}`}
            onClick={() => setTab(t.key)}
            style={{ cursor: 'pointer' }}
          >
            {t.label}
          </span>
        ))}
      </div>

      <div id="compliance-body" ref={reportRef}>
        {banner ? (
          <div
            style={{
              background: 'var(--banner-bg)',
              borderRadius: 12,
              padding: '20px 24px',
              marginBottom: 16,
              color: 'var(--banner-text)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 10,
              }}
            >
              <div>
                <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 20 }}>
                  {`${banner.companyShortName} — Compliance Report`}
                </div>
                <div style={{ color: 'var(--banner-sub)', fontSize: 12, marginTop: 3 }}>
                  {`${banner.monthLabel} · Generated ${new Date(banner.generatedAt).toLocaleString('en-GB')}`}
                </div>
              </div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 11, color: 'rgba(255,255,255,.5)' }}>
                {`${banner.workerCount} workers · ${banner.liveProjectCount} live projects`}
              </div>
            </div>
          </div>
        ) : null}

        {tab === 'overview' ? (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: 10,
                marginBottom: 16,
              }}
            >
              {KPI_CARDS.map((k) => (
                <div
                  key={k.label}
                  onClick={() => setTab(k.t)}
                  style={{
                    background: k.bg,
                    border: `1px solid ${k.color}`,
                    borderRadius: 10,
                    padding: '12px 14px',
                    textAlign: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 26, fontWeight: 400, color: k.color }}>
                    {k.val}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: k.color,
                      textTransform: 'uppercase',
                      letterSpacing: '.8px',
                      marginTop: 2,
                    }}
                  >
                    {k.label}
                  </div>
                </div>
              ))}
            </div>
            <ExpiredCertsSection rows={expiredCerts} onWorker={onWorker} />
            <ExpiringCertsSection rows={expiring30} onWorker={onWorker} />
            <MissingCertsSection rows={missingMandatory} onWorker={onWorker} />
            <SuppliersSection rows={nonCompliantSuppliers} />
            <UnsignedDocsSection rows={unsignedDocs} />
            <GeofenceSection rows={geofenceAnomalies} limit={20} />
            {dataAudit ? <DataQualitySection audit={dataAudit} onIssueClick={onIssueClick} /> : null}
          </>
        ) : null}

        {tab === 'expired' ? <ExpiredCertsSection rows={expiredCerts} onWorker={onWorker} /> : null}
        {tab === 'expiring' ? <ExpiringCertsSection rows={expiring30} onWorker={onWorker} /> : null}
        {tab === 'missing' ? <MissingCertsSection rows={missingMandatory} onWorker={onWorker} /> : null}
        {tab === 'suppliers' ? <SuppliersSection rows={nonCompliantSuppliers} /> : null}
        {tab === 'docs' ? <UnsignedDocsSection rows={unsignedDocs} /> : null}
        {tab === 'geofence' ? <GeofenceSection rows={geofenceAnomalies} limit={50} /> : null}
        {tab === 'dataquality' && dataAudit ? <DataQualitySection audit={dataAudit} onIssueClick={onIssueClick} /> : null}
      </div>

      {detailWorkerId ? (
        <WorkerDetail workerId={detailWorkerId} onClose={() => setDetailWorkerId(null)} />
      ) : null}
      {detailProjectId ? (
        <ProjectDetail projectId={detailProjectId} onClose={() => setDetailProjectId(null)} />
      ) : null}
    </div>
  );
}
